/**
 * Copyright 2023-2026 Nordcloud Oy or its affiliates. All Rights Reserved.
 */

import { useCallback, useEffect, useMemo, useState } from "react";
import { Col, Row } from "react-awesome-styled-grid";
import { Else, If, Then } from "react-if";
import { Box, FlexContainer, Text, theme } from "@nordcloud/gnui";
import {
  BatchAction,
  ExecutionPolicy,
  IncomingEventStatus,
} from "~/generated/graphql";
import { BaselineTag, NoData } from "~/components";
import { ToggleItemBox } from "~/components/ToggleItemBox/ToggleItemBox";
import { useElementNavigation } from "~/hooks";
import { isNonEmpty, sortByOrder } from "~/tools";
import { baselineTagToShow, getFlatEventEntities } from "~/utils";
import { getEventEntityStatus } from "~/views/events/helpers";
import { PlanEntityIncomingEventDetails } from "~/views/events/types";
import { ActionBatch } from "~/views/plans/components/ActionBatch";
import { ActionItemWrap, WrapMax } from "~/views/plans/styled";
import { useEvent } from "../../EventProvider";
import { IncomingEventActionBatchBox } from "./components/ActionBox/IncomingEventActionBatchBox";
import { IncomingEventActionBox } from "./components/ActionBox/IncomingEventActionBox";

type Props = {
  status?: IncomingEventStatus;
};

export function IncomingEventDetailsTab({ status }: Props) {
  const { event } = useEvent();
  const { currentHash, updateHash, scrollToElement } = useElementNavigation();
  const planActionBatches = event?.plan?.planActionBatches ?? [];

  const planEntities = useMemo(() => {
    const planActions = event?.plan?.planActions ?? [];

    return [...planActions, ...planActionBatches].map((entity) => {
      const { __typename, ...rest } = entity;
      return {
        ...rest,
        entityType: __typename,
        batchActions: entity.batchActions?.map((batchAction: BatchAction) => {
          const { __typename: batchActionTypeName, ...batchActionRest } =
            batchAction;
          return {
            ...batchActionRest,
            entityType: batchActionTypeName,
            executionPolicy: ExecutionPolicy.Anyway,
          };
        }),
      };
    });
  }, [event]);

  const sortedPlanEntities = useMemo(
    () => sortByOrder<PlanEntityIncomingEventDetails>(planEntities),
    [planEntities]
  );

  const flatEventEntities = useMemo(
    () => getFlatEventEntities(sortedPlanEntities),
    [sortedPlanEntities]
  );

  const [planEntity, setPlanEntity] = useState<PlanEntityIncomingEventDetails>(
    planEntities[0]
  );

  const switchPlanEntity = useCallback(
    (nextPlanEntity: PlanEntityIncomingEventDetails) => {
      const nextId = nextPlanEntity.id;
      if (nextId === planEntity.id) {
        return;
      }
      setPlanEntity(nextPlanEntity);
      const newHash = isBatch(nextPlanEntity.entityType)
        ? { batch: nextId, action: null }
        : { action: nextId, batch: null };
      updateHash(newHash, { merge: true });
    },
    [planEntity.id, setPlanEntity, updateHash]
  );

  useEffect(() => {
    const { action: hashActionId, batch: hashBatchId, tab } = currentHash || {};

    if ((hashActionId || hashBatchId) && (!tab || tab === "details")) {
      const entityToSet =
        flatEventEntities.find((item) => item.id === hashActionId) ||
        sortedPlanEntities.find((entity) => entity.id === hashBatchId);
      if (entityToSet) {
        setPlanEntity(entityToSet);
        const targetElementId = hashActionId
          ? `action-${hashActionId}`
          : `batch-${hashBatchId}`;
        scrollToElement(targetElementId);
      } else {
        // invalid id in hash - reset to first item
        setPlanEntity(sortedPlanEntities[0]);
        updateHash({ action: null, batch: null }, { merge: true });
      }
    }
  }, [
    currentHash,
    planEntities,
    sortedPlanEntities,
    flatEventEntities,
    scrollToElement,
    setPlanEntity,
    updateHash,
  ]);

  const isBatch = (entityType: string) => entityType === "PlanActionBatch";

  return (
    <Box innerSpacing="spacing00">
      <If condition={isNonEmpty(planEntities)}>
        <Then>
          <Row>
            <Col xs={4} sm={8} md={3} lg={3}>
              <FlexContainer direction="column" justifyContent="flex-start">
                <Text width="100%" align="left">
                  Actions
                </Text>
                {sortedPlanEntities?.map(
                  (item: PlanEntityIncomingEventDetails) => {
                    const baselineTagAction =
                      !isBatch(item.entityType) &&
                      baselineTagToShow(item, flatEventEntities);

                    return (
                      <If key={item.id} condition={isBatch(item.entityType)}>
                        <Then>
                          <WrapMax
                            css={{ paddingTop: `${theme.spacing.spacing03}` }}
                          >
                            <ActionBatch
                              id={item.id}
                              name={item.name}
                              onClick={() => switchPlanEntity(item)}
                              active={item.id === planEntity.id}
                              status={event?.status}
                            >
                              {item.batchActions?.map((batchAction) => {
                                const baselineTagBatchAction =
                                  baselineTagToShow(
                                    batchAction,
                                    flatEventEntities
                                  );

                                return (
                                  <div
                                    key={batchAction.id}
                                    id={`action-${batchAction.id}`}
                                  >
                                    <ToggleItemBox
                                      item={{
                                        id: batchAction.id,
                                        icon: getEventEntityStatus(status)
                                          ?.icon,
                                        iconColor:
                                          getEventEntityStatus(status)?.color(),
                                        tooltip:
                                          getEventEntityStatus(status)
                                            ?.description,
                                        name: batchAction.name,
                                        color: batchAction.action?.color,
                                        onMouseDown: () =>
                                          switchPlanEntity(batchAction),
                                      }}
                                      active={batchAction.id === planEntity.id}
                                      tag={
                                        baselineTagBatchAction ? (
                                          <BaselineTag
                                            tag={baselineTagBatchAction}
                                          />
                                        ) : undefined
                                      }
                                    />
                                  </div>
                                );
                              })}
                            </ActionBatch>
                          </WrapMax>
                        </Then>
                        <Else>
                          <WrapMax>
                            <ActionItemWrap id={`action-${item.id}`}>
                              <ToggleItemBox
                                item={{
                                  id: item.id,
                                  icon: getEventEntityStatus(status)?.icon,
                                  iconColor:
                                    getEventEntityStatus(status)?.color(),
                                  tooltip:
                                    getEventEntityStatus(status)?.description,
                                  color: item.action?.color ?? "grey",
                                  name: item.name,
                                  onMouseDown: () => switchPlanEntity(item),
                                }}
                                active={item.id === planEntity.id}
                                tag={
                                  baselineTagAction ? (
                                    <BaselineTag tag={baselineTagAction} />
                                  ) : undefined
                                }
                              />
                            </ActionItemWrap>
                          </WrapMax>
                        </Else>
                      </If>
                    );
                  }
                )}
              </FlexContainer>
            </Col>
            <Col xs={4} sm={8} md={5} lg={9}>
              <Box boxStyle="grey" innerSpacing="spacing04">
                <FlexContainer alignItems="flex-start">
                  <If condition={isBatch(planEntity?.entityType)}>
                    <Then>
                      <IncomingEventActionBatchBox
                        planActionBatch={planEntity}
                        planId={event?.plan?.id}
                      />
                    </Then>
                    <Else>
                      <IncomingEventActionBox
                        planEntity={planEntity}
                        planId={event?.plan?.id}
                        status={event?.status}
                      />
                    </Else>
                  </If>
                </FlexContainer>
              </Box>
            </Col>
          </Row>
        </Then>
        <Else>
          <NoData hasIcon message="There are no Actions" />
        </Else>
      </If>
    </Box>
  );
}
